import type { RefObject } from "react";
import type { HlsVideoElement } from "hls-video-element";
import HlsVideo from "hls-video-element/react";
import { t } from "@/i18n-js/instance";
import { useVideoAnalytics } from "@/react/components/AnalyticsTracker/useVideoAnalytics";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useMediaController } from "./hooks/useMediaController";
import { useVideoFullscreen } from "./hooks/useVideoFullscreen";
import { useVideoPreviewThumbnails } from "./hooks/useVideoPreviewThumbnails";
import { useVideoProgress } from "./hooks/useVideoProgress";
import { useVideoProgressStore } from "./hooks/useVideoProgressStore";
import type { VideoMetadata } from "./types";

const thumbnailsTrackLabel = "thumbnails";

interface PlayerProps {
  captionsUrl?: string;
  chaptersUrl?: string;
  className?: string;
  contentType?: string;
  isHls?: boolean;
  isVideoAnalyticsEnabled?: boolean;
  onLoadedMetadata?: (metadata: VideoMetadata) => void;
  onTimeUpdate: (time: number) => void;
  poster?: string;
  preload?: "none" | "metadata" | "auto";
  previewThumbnailsUrl?: string;
  signedId: string;
  src: string;
  videoRef: RefObject<HlsVideoElement>;
}

export const Player = ({
  className,
  src,
  contentType,
  captionsUrl,
  chaptersUrl,
  previewThumbnailsUrl,
  preload = "none",
  poster,
  signedId,
  isHls = false,
  isVideoAnalyticsEnabled = true,
  onLoadedMetadata,
  onTimeUpdate,
  videoRef,
}: PlayerProps) => {
  const { currentTime } = useVideoProgress({
    onTimeUpdate,
    onLoadedMetadata,
    videoRef,
  });

  const { clearStoredProgress } = useVideoProgressStore({
    currentTime,
    signedId,
    videoRef,
  });

  useVideoAnalytics(videoRef, signedId, isVideoAnalyticsEnabled);

  const { controller, dispatchMediaEvent } = useMediaController({
    videoRef,
  });

  const { handleVideoClick } = useVideoFullscreen({
    controller,
    dispatchMediaEvent,
  });

  const { blobUrl: previewThumbnailsBlobUrl } = useVideoPreviewThumbnails({
    previewThumbnailsUrl,
  });

  if (isHls) {
    return (
      <HlsVideo
        ref={videoRef}
        className={className}
        slot="media"
        src={src}
        poster={poster}
        preload={preload}
        onClick={handleVideoClick}
        onEnded={clearStoredProgress}
        onContextMenu={e => e.preventDefault()}
        playsInline
      >
        {captionsUrl && (
          <track
            key={captionsUrl}
            kind="captions"
            label={t("default")}
            src={captionsUrl}
            srcLang="en"
            default
          />
        )}
        {chaptersUrl && <track kind="chapters" src={chaptersUrl} default />}
        {previewThumbnailsBlobUrl && (
          <track
            kind="metadata"
            label={thumbnailsTrackLabel}
            src={previewThumbnailsBlobUrl}
            default
          />
        )}
      </HlsVideo>
    );
  }

  return (
    <video
      ref={videoRef}
      className={classNames(
        "[&::-webkit-media-text-track-container]:[transform:var(--media-webkit-text-track-transform)]",
        className,
      )}
      slot="media"
      poster={poster}
      preload={preload}
      onClick={handleVideoClick}
      onEnded={clearStoredProgress}
      onContextMenu={e => e.preventDefault()}
      playsInline
    >
      <source src={src} type={contentType} />
      {captionsUrl && (
        <track
          key={captionsUrl}
          kind="captions"
          label={t("default")}
          src={captionsUrl}
          srcLang="en"
          default
        />
      )}
      {chaptersUrl && <track kind="chapters" src={chaptersUrl} default />}
      {previewThumbnailsBlobUrl && (
        <track
          kind="metadata"
          label={thumbnailsTrackLabel}
          src={previewThumbnailsBlobUrl}
          default
        />
      )}
    </video>
  );
};
